import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Form } from "react-bootstrap";
import { FluentDropdown } from "../../../../../../components/FluentUI/Fluent-Dropdown.jsx";
import DisabledPlus from "../../../../../../assets/icons/Icon - Add - Disabled.svg";
import SyncMemberPermissions, { validateSyncConfig } from "./SyncMemberPermissions.jsx";
import { GetAllUsersNew } from "../../../../../../services/api/user-api";
import { GetAllGroupsAPI } from "../../../../../../services/api/group-api";

const statusShape = PropTypes.shape({
  Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  Name: PropTypes.string,
  Description: PropTypes.string,
  IsBase: PropTypes.bool,
  IsInitialStatus: PropTypes.bool,
  AutomaticTriggers: PropTypes.string,
});

const BUILTIN_DEFAULT_BADGE = {
  Id: 8,
  Name: "Blue",
  BackgroundColor: "#e4f0ff",
  FontColor: "#004aa4",
  BorderColor: "#004aa4",
};

const resolveDefaultBadge = (badgeColorOptions = []) => {
  const byName = badgeColorOptions.find(
    c => typeof c?.Name === "string" && c.Name.trim().toLowerCase() === "blue"
  );
  return byName || BUILTIN_DEFAULT_BADGE;
};

const EditWorkflowStatusModal = ({
  isStatusModalOpen,
  setIsStatusModalOpen,
  openWorkflowStatus,
  workflowStatuses,
  setWorkflowStatuses,
  workflowBaseStatuses,
  setWorkflowBaseStatuses,
  workflowBasic,
  setWorkflowBasic,
  badgeColorOptions = [],
}) => {
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState([]);
  const [currentBadge, setCurrentBadge] = useState(null);
  const [currentBadgeName, setCurrentBadgeName] = useState("");
  const [departmentMembers, setDepartmentMembers] = useState([]);

  const isInitialStatus =
    !!selectedWorkflowStatus?.IsInitialStatus ||
    (!!workflowBasic?.InitialStatus?.name &&
      workflowBasic.InitialStatus.name === selectedWorkflowStatus?.Name);

  useEffect(() => {
    if (openWorkflowStatus.Id === undefined) return;
    setSelectedWorkflowStatus(openWorkflowStatus);
    setIsStatusModalOpen(true);
  }, [openWorkflowStatus, setIsStatusModalOpen]);

  useEffect(() => {
    if (!selectedWorkflowStatus || selectedWorkflowStatus.Id === undefined) return;

    const optionFromId = badgeColorOptions.find(
      c => String(c?.Id) === String(selectedWorkflowStatus?.BadgeColorId)
    );

    const nextBadge =
      selectedWorkflowStatus?.BadgeColor ??
      optionFromId ??
      null;

    if (nextBadge === null) {
      const fallback = resolveDefaultBadge(badgeColorOptions);
      setSelectedWorkflowStatus(prev => ({
        ...prev,
        BadgeColorId: fallback?.Id ?? null,
        BadgeColor: fallback,
      }));
      setCurrentBadge(fallback);
      setCurrentBadgeName(fallback?.Name ?? "");
      return;
    }

    setCurrentBadge(nextBadge);
    setCurrentBadgeName(
      selectedWorkflowStatus?.BadgeColor?.Name ||
      optionFromId?.Name ||
      ""
    );
  }, [selectedWorkflowStatus, badgeColorOptions]);

  // Load department Users + Groups for the Automation picker (only for the Initial Status).
  useEffect(() => {
    if (!isStatusModalOpen || !isInitialStatus || departmentMembers.length > 0) return;
    let cancelled = false;
    const query = { pageSize: 100000, pageNumber: 1, sortDesc: false, sortBy: "name", filters: [] };
    Promise.all([GetAllUsersNew(query), GetAllGroupsAPI(query)])
      .then(([usersRes, groupsRes]) => {
        if (cancelled) return;
        const users = (usersRes?.Users || usersRes?.data?.Users || []).map((u) => ({
          Id: u.Id ?? u.UserId,
          Name: u.Name ?? u.FullName ?? u.UserName,
          IsGroup: false,
          // Intermediate user state: still in the Department but deactivated (Admin > Users).
          Inactive: u.Active === false,
        }));
        const groups = (groupsRes?.data?.dataList || []).map((g) => ({
          Id: g.GroupId ?? g.Id,
          Name: g.Name,
          IsGroup: true,
          // Intermediate group state: still in the Department but deactivated (Admin > Groups).
          Inactive: g.Active === false,
          // The group's member roster (legacy lowercase shape from GetAllGroups), used for the
          // "Group Members" hover on selected chips — parity with the Assign Members component.
          Members: (g.ViewMembers || [])
            .map((vm) => ({
              Id: vm.userID ?? vm.UserId ?? vm.Id,
              Name: vm.userNameFull ?? vm.userName ?? vm.Name,
            }))
            .filter((vm) => vm.Id !== null && vm.Id !== undefined && vm.Name),
        }));
        setDepartmentMembers(
          [...users, ...groups].filter((m) => m.Id !== null && m.Id !== undefined && m.Name)
        );
      })
      .catch(() => {
        /* Non-blocking: the picker simply shows no suggestions if the load fails. */
      });
    return () => {
      cancelled = true;
    };
  }, [isStatusModalOpen, isInitialStatus, departmentMembers.length]);

  const handleClose = () => {
    setSelectedWorkflowStatus({});
    setIsStatusModalOpen(false);
  };

  const handleBadgeNameChange = (name) => {
    const color =
      badgeColorOptions.find(c => c?.Name === name) ??
      resolveDefaultBadge(badgeColorOptions);

    setCurrentBadge(color);
    setCurrentBadgeName(color?.Name ?? "");

    setSelectedWorkflowStatus(prev => ({
      ...prev,
      BadgeColorId: color?.Id ?? null,
      BadgeColor: color,
    }));
  };

  const addUpdateCurrentWorkflowStatus = () => {
    // Validate the Sync config ONLY when the Automation section is actually shown (Initial
    // Status) — it's the only place the inline error can surface. Same gate as the render at
    // `{isInitialStatus && <SyncMemberPermissions/>}`. Otherwise a non-initial status carrying a
    // stale/invalid payload (e.g. after the Initial Status is re-pointed mid-session) would block
    // Save with no visible feedback. The backend discards Sync config on non-initial statuses.
    const syncError = isInitialStatus
      ? validateSyncConfig(selectedWorkflowStatus?.SyncMemberPermissions)
      : "";
    if (syncError) {
      // Keep the modal open; the Automation section renders the inline error.
      return;
    }

    const isBase = !!selectedWorkflowStatus.IsBase;

    let nextStatus = { ...selectedWorkflowStatus };

    const hasTaskCreation =
      (nextStatus.AutomaticTriggers || "")
        .split(",")
        .map(t => t.trim())
        .includes("Task Creation");

    if (hasTaskCreation && !nextStatus.IsInitialStatus) {
      nextStatus = {
        ...nextStatus,
        IsInitialStatus: true,
        AutomaticTriggers: (nextStatus.AutomaticTriggers || "")
          .split(",")
          .map(t => t.trim())
          .filter(t => t && t !== "Task Creation")
          .join(","),
      };

      setWorkflowBasic(prev => ({
        ...prev,
        InitialStatus: {
          id: nextStatus.Name,
          name: nextStatus.Name,
          isBase: isBase,
        },
      }));

      const stripTaskCreation = s => ({
        ...s,
        IsInitialStatus: false,
        AutomaticTriggers: (s.AutomaticTriggers || "")
          .split(",")
          .map(t => t.trim())
          .filter(t => t && t !== "Task Creation")
          .join(","),
      });

      if (isBase) {
        setWorkflowStatuses(prev => prev.map(stripTaskCreation));
      } else {
        setWorkflowBaseStatuses(prev => prev.map(stripTaskCreation));
      }
    }

    const upsert = setter =>
      setter(prev => {
        const exists = prev.some(s => String(s.Id) === String(nextStatus.Id));
        return exists
          ? prev.map(s => (String(s.Id) === String(nextStatus.Id) ? { ...s, ...nextStatus } : s))
          : [...prev, nextStatus];
      });

    if (isBase) {
      upsert(setWorkflowBaseStatuses);
    } else {
      upsert(setWorkflowStatuses);
    }

    handleClose();
  };

  return (
    <Modal
      show={isStatusModalOpen}
      className="workflow-modal-panel"
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Task Status</Modal.Title>
      </Modal.Header>

      <Modal.Body className="workflow-modal-body">
        {/* STATUS NAME */}
        <div className="workflow-modal-section">
          <img src={DisabledPlus} alt="" />
          <Form.Control
            type="text"
            value={selectedWorkflowStatus?.Name || ""}
            onChange={(e) => {
              setSelectedWorkflowStatus({
                ...selectedWorkflowStatus,
                Name: e.target.value,
              });
            }}
            placeholder="Enter task status name"
            disabled={selectedWorkflowStatus?.IsBase}
          />
        </div>
        <hr className="divider" />

        {/* STATUS DESCRIPTION */}
        <div className="workflow-modal-section">
          <div className="workflow-modal-description">
            <div className="workflow-modal-description-title">Description</div>
            <div className="workflow-modal-description-subtitle">
              The description for the customized task status
            </div>
          </div>
          <div className="workflow-modal-input">
            <Form>
              <Form.Group className="description">
                <Form.Control
                  id="description"
                  name="description"
                  autoComplete="off"
                  placeholder="Describe how people should use this status"
                  className="project-textarea textarea"
                  value={selectedWorkflowStatus?.Description || ""}
                  onChange={(e) => {
                    setSelectedWorkflowStatus({
                      ...selectedWorkflowStatus,
                      Description: e.target.value,
                    });
                  }}
                  as="textarea"
                  rows="3"
                  disabled={selectedWorkflowStatus?.IsBase}
                />
              </Form.Group>
            </Form>
          </div>
        </div>

        {/* BADGE COLOR */}
        <hr className="divider" />
        <div className="workflow-modal-section">
          <div className="workflow-modal-description">
            <div className="workflow-modal-description-title">Badge Color</div>
            <div className="workflow-modal-description-subtitle">
              Choose a badge color
            </div>
          </div>

          <div className="workflow-modal-input">
            <FluentDropdown
              value={currentBadgeName}
              disabled={selectedWorkflowStatus?.IsBase}
              isControlled={true}
              options={badgeColorOptions.map(c => c?.Name).filter(Boolean)}
              selectedOptions={[currentBadgeName]}
              onChange={(e, item) => {
                if (!item?.optionValue) return;
                handleBadgeNameChange(item.optionValue);
              }}
              labelStyle="font-weight-normal"
            />
          </div>
        </div>

        {isInitialStatus && (
          <>
            <hr className="divider" />
            <SyncMemberPermissions
              value={selectedWorkflowStatus?.SyncMemberPermissions}
              departmentMembers={departmentMembers}
              onChange={(config) =>
                setSelectedWorkflowStatus((prev) => ({ ...prev, SyncMemberPermissions: config }))
              }
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button className="btn btn-setting-light btn-sm" onClick={handleClose}>
          Close
        </Button>
        <Button
          className="btn btn-setting-primary btn-sm"
          onClick={addUpdateCurrentWorkflowStatus}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EditWorkflowStatusModal.propTypes = {
  isStatusModalOpen: PropTypes.bool.isRequired,
  setIsStatusModalOpen: PropTypes.func.isRequired,
  openWorkflowStatus: statusShape.isRequired,
  workflowStatuses: PropTypes.arrayOf(statusShape).isRequired,
  setWorkflowStatuses: PropTypes.func.isRequired,
  workflowBaseStatuses: PropTypes.arrayOf(statusShape).isRequired,
  setWorkflowBaseStatuses: PropTypes.func.isRequired,
  workflowBasic: PropTypes.shape({
    InitialStatus: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      isBase: PropTypes.bool,
    }),
  }).isRequired,
  setWorkflowBasic: PropTypes.func.isRequired,
  badgeColorOptions: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Name: PropTypes.string.isRequired,
    BackgroundColor: PropTypes.string,
    FontColor: PropTypes.string,
    BorderColor: PropTypes.string,
  })).isRequired,
};

export default EditWorkflowStatusModal;
